/**
 * Temu 发品 / 编辑确认前预检：
 * 1) Temu 发品约束 GET .../publish/standard（非 Shein fill-in-standard）
 * 2) 币种须落在 CNY/USD（或站点列表，若自运营已接入）
 * 3) 商家 SKU 本地必填与长度
 *
 * Shein 专属接口（关联属性规则、supplierSku 平台查重、fill_in_standard_list）不在 Temu 路径调用。
 */

import { temuPlatformAPI } from '@/utils/api';
import {
  parseFillInStandardMap,
  resolveSkuPackageCapabilities,
} from '@/temu-platform/utils/temuPublishFillInStandard';
import { validateShopPublishCurrency } from '@/temu-platform/utils/resolveTemuPublishCurrency';
import { temuShopCapabilities } from '@/temu-platform/utils/temuShopCapabilities';
import { extractSiteListCurrencies } from '@/temu-platform/utils/temuSiteList';
import {
  resolveTemuCat1Id,
  validateTemuProductNameLength,
} from '@/temu-platform/utils/temuProductNameLength';

export { parseFillInStandardMap };

/** 商家 SKU / 货号须含短横线分段（如 SPU-颜色-尺码）；允许末尾有/无预留「-」 */
export function hasReservedOrFilledHyphenCode(value) {
  const raw = String(value ?? '').trim();
  if (!raw || !raw.includes('-')) return false;
  if (raw.endsWith('-')) return raw.replace(/-+$/, '').length > 0;
  const parts = raw.split('-').filter(Boolean);
  return parts.length >= 2;
}

function fail(message, section = 'basic', field = 'form') {
  return { ok: false, message, section, field };
}

function unwrapInfo(raw) {
  if (!raw || typeof raw !== 'object') return raw;
  if (raw.info != null) return raw.info;
  return raw;
}

function isTemuPublishStandard(raw) {
  const info = unwrapInfo(raw) || {};
  return info.temuPublishStandard === true
    || String(info.source || '').toLowerCase() === 'temu';
}

function collectSupplierSkuRows(form = {}) {
  const rows = [];
  (form.skcList || []).forEach((skc, skcIndex) => {
    (skc?.skuRows || []).forEach((sku, skuIndex) => {
      const supplierSku = String(sku?.supplierSku || '').trim();
      if (!supplierSku) return;
      const originalSupplierSku = String(
        sku?.originalSupplierSku || sku?.original_supplier_sku || '',
      ).trim();
      rows.push({
        supplierSku,
        originalSupplierSku,
        skuCode: String(sku?.skuCode || sku?.platformSku || '').trim(),
        skcIndex,
        skuIndex,
        colorLabel: skc?.colorLabel || skc?.colorExtraValue || `规格${skcIndex + 1}`,
        sizeLabel: sku?.sizeLabel || sku?.sizeValueId || `SKU${skuIndex + 1}`,
      });
    });
  });
  return rows;
}

/**
 * 本地：商家 SKU 必填、长度；须含短横线分段（如 SPU-颜色-尺码）；图案可选接在末段
 */
export function validateSupplierSkusLocal(form = {}) {
  const rows = collectSupplierSkuRows(form);
  if (!rows.length) {
    return fail('请为每个 SKU 填写商家 SKU（supplier_sku）', 'spec', 'supply');
  }
  for (const row of rows) {
    if (row.supplierSku.length > 200) {
      return fail(
        `「${row.colorLabel} / ${row.sizeLabel}」商家 SKU 不能超过 200 个字符`,
        'spec',
        'supply',
      );
    }
    if (!hasReservedOrFilledHyphenCode(row.supplierSku)) {
      return fail(
        `「${row.colorLabel} / ${row.sizeLabel}」商家 SKU 须含短横线「-」（如 SPU-颜色-尺码；勾选添加图案时再接图案编号）`,
        'spec',
        'supply',
      );
    }
  }
  for (const skc of form.skcList || []) {
    for (const sku of skc?.skuRows || []) {
      if (!String(sku?.supplierSku || '').trim()) {
        const color = skc?.colorLabel || skc?.colorExtraValue || '规格';
        const size = sku?.sizeLabel || sku?.sizeValueId || 'SKU';
        return fail(`请填写「${color} / ${size}」商家 SKU`, 'spec', 'supply');
      }
    }
  }
  return { ok: true };
}

/**
 * Temu 发品约束校验（非 Shein fill_in_standard_list）。
 */
function validateAgainstTemuStandard(form, standardRaw, attrTpl) {
  const info = unwrapInfo(standardRaw) || {};
  // 标题上限以一级类目加权规则为准（公告）；店铺标准若更严则再收紧
  const catLimit = validateTemuProductNameLength(
    String(form.title || form.productName || ''),
    resolveTemuCat1Id(form),
  );
  if (!catLimit.ok) {
    return fail(catLimit.message, 'basic', 'title');
  }
  const titleEnCheck = validateTemuProductNameLength(
    String(form.titleEn || form.englishTitle || ''),
    resolveTemuCat1Id(form),
  );
  if (!titleEnCheck.ok) {
    return fail(titleEnCheck.message.replace('商品名称', '英文名称'), 'basic', 'titleEn');
  }
  const maxLen = Number(
    info.titleMaxLength
    ?? info.productNameMaxLength
    ?? info.default_language_title_max_length
    ?? 0,
  );
  const title = String(form.title || form.productName || '');
  if (maxLen > 0 && catLimit.weight > maxLen) {
    return fail(`商品名称超过店铺上限（加权 ${catLimit.weight}/${maxLen}）`, 'basic', 'title');
  }
  const titleEn = String(form.titleEn || form.englishTitle || '');
  if (maxLen > 0 && titleEnCheck.weight > maxLen) {
    return fail(`英文名称超过店铺上限（加权 ${titleEnCheck.weight}/${maxLen}）`, 'basic', 'titleEn');
  }

  // Temu 不走 Shein quantity_info / package_type；保持安全默认不强制件数
  const caps = resolveSkuPackageCapabilities(standardRaw);
  void attrTpl;
  void title;
  void titleEn;
  return {
    ok: true,
    standardMap: parseFillInStandardMap(standardRaw),
    standardInfo: info,
    skuPkgCaps: caps,
  };
}

/**
 * @param {object} opts
 * @param {string|number} opts.shopId
 * @param {object} opts.form
 * @param {boolean} [opts.isEdit]
 * @param {object|null} [opts.attrTpl]
 * @param {string} [opts.shopType]
 * @param {object|null} [opts.shopCaps]
 * @param {object|null} [opts.siteList]
 * @param {Map|null} [opts.standardCache] 同店同类目发品规范缓存（批量核验复用）
 */
export async function runTemuPublishPreflight({
  shopId,
  form,
  isEdit = false,
  attrTpl = null,
  shopType = '',
  shopCaps = null,
  siteList = null,
  standardCache = null,
}) {
  const localSku = validateSupplierSkusLocal(form);
  if (!localSku.ok) return localSku;

  const categoryId = Number(form.categoryId);
  const cacheKey = `${shopId}:${Number.isFinite(categoryId) ? categoryId : ''}`;
  let standardRaw = null;
  try {
    if (standardCache?.has(cacheKey)) {
      standardRaw = await standardCache.get(cacheKey);
    } else {
      const pending = temuPlatformAPI.getPublishFillInStandard(shopId, {
        catId: Number.isFinite(categoryId) ? categoryId : undefined,
        category_id: Number.isFinite(categoryId) ? categoryId : undefined,
      });
      if (standardCache) {
        standardCache.set(cacheKey, pending);
        pending.catch(() => { standardCache.delete(cacheKey); });
      }
      standardRaw = await pending;
    }
  } catch (ex) {
    return fail(ex?.message || '查询 Temu 发品规范失败，请稍后重试', 'basic', 'form');
  }

  if (!isTemuPublishStandard(standardRaw) && standardRaw != null) {
    // 兼容异常回包：仍按 Temu 路径宽松校验，不按 Shein field_key 拦截
  }
  const std = validateAgainstTemuStandard(form, standardRaw, attrTpl);
  if (!std.ok) return std;

  const caps = shopCaps || temuShopCapabilities(shopType || form?.shopType);
  let resolvedSiteList = siteList || form?.siteList || null;
  if (caps.usesPriceInfo && !extractSiteListCurrencies(resolvedSiteList).length && shopId) {
    try {
      const siteRes = await temuPlatformAPI.getShopSiteList(shopId, { refresh: true });
      resolvedSiteList = siteRes?.siteList ?? resolvedSiteList;
    } catch {
      // 自运营站点列表未接入时，币种校验会回退到 publish/standard
    }
  }
  const currencyCheck = validateShopPublishCurrency(form, {
    shopCaps: caps,
    shopType: shopType || form?.shopType,
    siteList: resolvedSiteList,
    standardRaw,
  });
  if (!currencyCheck.ok) {
    // 自运营站点未接入且规范有币种时，上面已回退；此处仅真实失败才拦
    return currencyCheck;
  }

  // Temu 暂无 Shein 关联属性规则 / supplierSku 平台查重；不调用对应 stub，避免误伤
  void isEdit;
  return {
    ok: true,
    standardRaw,
    standardMap: std.standardMap,
    standardInfo: std.standardInfo,
    skuPkgCaps: std.skuPkgCaps,
    siteList: resolvedSiteList,
  };
}
